import React, { useEffect, useRef } from 'react';
import { getTheme } from '../utils/theme';

const theme = getTheme();

export default function CallOverlay({
  callState, callType, callerName, isMuted, isVideoOff,
  localVideoRef, remoteVideoRef,
  onAccept, onReject, onEnd, onToggleMute, onToggleVideo,
}) {
  // Dedicated <audio> element for audio-only calls.
  // Some mobile browsers don't autoplay audio through a hidden <video>,
  // so we mirror the stream to a visible <audio> element.
  const audioElRef = useRef(null);

  // Mirror remote stream to <audio> element for audio-only calls
  useEffect(() => {
    if (callType !== 'audio' || callState !== 'active') return;
    const videoEl = remoteVideoRef?.current;
    const audioEl = audioElRef.current;
    if (!audioEl) return;

    const syncStream = () => {
      if (videoEl?.srcObject && audioEl.srcObject !== videoEl.srcObject) {
        audioEl.srcObject = videoEl.srcObject;
        audioEl.play().catch(err => {
          // On mobile, autoplay may be blocked until user interaction
          console.warn('[CallOverlay] audio autoplay blocked:', err.message);
        });
      }
    };

    syncStream();
    const id = setInterval(syncStream, 400);
    return () => clearInterval(id);
  }, [callState, callType, remoteVideoRef]);

  if (callState === 'idle') return null;

  return (
    <div style={styles.overlay}>

      {/* ── Video call streams ── */}
      {callType === 'video' && (
        <div style={styles.videoContainer}>
          <video ref={remoteVideoRef} autoPlay playsInline style={styles.remoteVideo} />
          <video ref={localVideoRef}  autoPlay playsInline muted style={styles.localVideo} />
          {/* Show "no video" placeholder if remote video is off */}
        </div>
      )}

      {/* ── Audio call: hidden video + audible audio element ── */}
      {callType === 'audio' && (
        <>
          {/* Keep remoteVideoRef attached so stream arrives via ontrack */}
          <video ref={remoteVideoRef} autoPlay playsInline style={{ width: 1, height: 1, opacity: 0.01, position: 'absolute' }} />
          <audio ref={audioElRef} autoPlay playsInline controls={false} style={{ width: 1, height: 1, opacity: 0.01, position: 'absolute' }} />
        </>
      )}

      {/* ── Audio call active UI ── */}
      {callType === 'audio' && callState === 'active' && (
        <div style={styles.audioContainer}>
          <div style={styles.callerAvatar}>{callerName?.[0]?.toUpperCase() || '?'}</div>
          <p style={styles.callerName}>{callerName || 'Audio Call'}</p>
          <div style={styles.pulseRing} />
          <p style={styles.audioText}>🎙️ Audio call active</p>
        </div>
      )}

      {/* ── Incoming call ── */}
      {callState === 'receiving' && (
        <div style={styles.incomingContainer}>
          <div style={styles.callerAvatar}>{callerName?.[0]?.toUpperCase() || '?'}</div>
          <p style={styles.callerName}>{callerName}</p>
          <p style={styles.callTypeLabel}>Incoming {callType} call...</p>
          <div style={styles.incomingButtons}>
            <button onClick={onAccept} style={{ ...styles.btn, ...styles.acceptBtn }}>
              {callType === 'video' ? '📹 Accept' : '📞 Accept'}
            </button>
            <button onClick={onReject} style={{ ...styles.btn, ...styles.rejectBtn }}>
              📵 Decline
            </button>
          </div>
        </div>
      )}

      {/* ── Outgoing call (ringing) ── */}
      {callState === 'calling' && (
        <div style={styles.incomingContainer}>
          <div style={styles.callerAvatar}>
            {callType === 'video' ? '📹' : '📞'}
          </div>
          <p style={styles.callerName}>Calling...</p>
          <p style={styles.callTypeLabel}>{callType} call</p>
          <button onClick={onEnd} style={{ ...styles.btn, ...styles.rejectBtn }}>Cancel</button>
        </div>
      )}

      {/* ── Active call controls ── */}
      {callState === 'active' && (
        <div style={styles.controls}>
          <button onClick={onToggleMute}
            style={{ ...styles.controlBtn, ...(isMuted ? styles.activeControl : {}) }}
            title={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? '🔇' : '🎤'}
          </button>
          {callType === 'video' && (
            <button onClick={onToggleVideo}
              style={{ ...styles.controlBtn, ...(isVideoOff ? styles.activeControl : {}) }}
              title={isVideoOff ? 'Turn camera on' : 'Turn camera off'}>
              {isVideoOff ? '📵' : '📹'}
            </button>
          )}
          <button onClick={onEnd} style={{ ...styles.controlBtn, ...styles.endCallBtn }}
            title="End call">
            📵 End
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)',
    zIndex: 100000, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  videoContainer: {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  remoteVideo: {
    width: '100%', height: '100%', objectFit: 'cover',
    background: '#111',
  },
  localVideo: {
    position: 'absolute', bottom: 90, right: 16,
    width: 120, height: 90, objectFit: 'cover',
    borderRadius: 10, border: '2px solid rgba(255,255,255,0.6)',
    transform: 'scaleX(-1)',
    zIndex: 2,
  },
  audioContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
    padding: '40px 32px',
    background: `rgba(${theme.colors.primaryRGB},0.08)`,
    borderRadius: 24,
    border: `1px solid rgba(${theme.colors.primaryRGB},0.2)`,
    backdropFilter: 'blur(12px)',
    position: 'relative', zIndex: 10,
  },
  pulseRing: {
    width: 100, height: 100, borderRadius: '50%',
    border: `3px solid rgba(${theme.colors.primaryRGB},0.6)`,
    animation: 'pulse 1.5s ease-in-out infinite',
    position: 'absolute',
  },
  audioText: { color: '#a0aec0', fontSize: 14, margin: 0 },
  incomingContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
    padding: '40px 32px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 24,
    backdropFilter: 'blur(12px)',
    position: 'relative', zIndex: 10,
  },
  callerAvatar: {
    width: 80, height: 80, borderRadius: '50%',
    background: theme.colors.gradient,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32, color: '#fff', fontWeight: 700,
  },
  callerName:    { color: '#fff', fontSize: 20, fontWeight: 600, margin: 0, textAlign: 'center' },
  callTypeLabel: { color: '#a0aec0', fontSize: 13, margin: 0 },
  incomingButtons: { display: 'flex', gap: 16, marginTop: 8 },
  controls: {
    position: 'absolute', bottom: 36, zIndex: 20,
    display: 'flex', gap: 16, alignItems: 'center',
  },
  btn: {
    padding: '13px 26px', borderRadius: 50, border: 'none',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  acceptBtn: { background: '#22c55e', color: '#fff' },
  rejectBtn: { background: '#ef4444', color: '#fff' },
  controlBtn: {
    width: 58, height: 58, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    fontSize: 20, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(8px)',
  },
  activeControl: { background: 'rgba(239,68,68,0.7)' },
  endCallBtn: {
    width: 'auto', padding: '0 26px', borderRadius: 50,
    background: '#ef4444', fontSize: 14,
  },
};
